import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Plus, Wrench, ImagePlus, X, Pencil, Trash2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { centsToDisplay, dollarsInputToCents } from "@/lib/money";
import { ServiceCoverThumb } from "@/components/org/ServiceCoverThumb";
import { ServiceEditModal } from "@/components/org/ServiceEditModal";
import { ImageStyleSelector } from "@/components/org/ImageStyleSelector";
import { usePolling } from "@/hooks/usePolling";
import { LastUpdated } from "@/components/common/LastUpdated";
import { AddingProgressBar } from "@/components/common/AddingProgressBar";

const PRICE_TYPES = [
  { value: "fixed", label: "Fixed price" },
  { value: "starting_at", label: "Starting price" },
  { value: "custom_quote", label: "Custom quote" },
];

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

export default function OrgServices() {
  const { API, user } = useAuth();
  const isOwner = user?.role === "org_owner";
  const isManager = isOwner || user?.role === "org_admin";
  const canManageImages = isOwner || user?.role === "org_admin" || (user?.role !== "team_lead" && !!user?.catalog_manage);
  const [services, setServices] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const successTimer = useRef(null);
  const [form, setForm] = useState({ name: "", description: "", service_type: "", duration_minutes: "", price_type: "fixed", price: "" });
  const [saving, setSaving] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [stagedFile, setStagedFile] = useState(null);
  const [stagedPreview, setStagedPreview] = useState(null);
  const [imageStyle, setImageStyle] = useState("flat");
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const fileInputRef = useRef(null);
  const styleUserChangedRef = useRef(false);

  const load = useCallback(async (silent) => {
    try {
      const { data } = await axios.get(`${API}/org/services`, { withCredentials: true });
      setServices(data.services || []);
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/org/image-styles`, { withCredentials: true })
      .then(({ data }) => { if (!styleUserChangedRef.current) setImageStyle(data.service_image_style || "flat"); })
      .catch(() => {});
  }, [API]);

  const hasPendingImages = (services || []).some((s) => s.image_generation_status === "pending");

  // Skip the silent auto-refresh while adding a service or a row action is in flight.
  // Poll faster while any service is generating its AI image so placeholders resolve quickly.
  usePolling(() => {
    if (saving || savingId) return;
    load(true);
  }, hasPendingImages ? 3000 : 10000);

  const pickFile = (file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) { setError("Please choose a PNG, JPEG, or WEBP image."); return; }
    if (file.size > MAX_BYTES) { setError("Image must be smaller than 10MB."); return; }
    setError("");
    setStagedFile(file);
    setStagedPreview(URL.createObjectURL(file));
  };

  const clearStagedFile = () => {
    setStagedFile(null);
    setStagedPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addService = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/org/services`, {
        name: form.name,
        description: form.description || null,
        service_type: form.service_type || null,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes, 10) : null,
        price_type: form.price_type,
        price_cents: form.price_type === "custom_quote" ? null : dollarsInputToCents(form.price),
        active: true,
        skip_ai_image: !!stagedFile,
      }, { withCredentials: true, timeout: 20000 });
      if (stagedFile) {
        const fd = new FormData();
        fd.append("file", stagedFile);
        await axios.post(`${API}/org/services/${data.service.service_id}/images`, fd, { withCredentials: true });
      }
      setForm({ name: "", description: "", service_type: "", duration_minutes: "", price_type: "fixed", price: "" });
      clearStagedFile();
      await load();
      setSuccess(`"${data.service.name}" added${stagedFile ? "." : " — generating its image now."}`);
      clearTimeout(successTimer.current);
      successTimer.current = setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (service) => {
    setSavingId(service.service_id);
    try {
      await axios.patch(`${API}/org/services/${service.service_id}`, { active: !service.active }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const removeService = async (service) => {
    setSavingId(service.service_id);
    setError("");
    try {
      await axios.delete(`${API}/org/services/${service.service_id}`, { withCredentials: true });
      setConfirmDeleteId(null);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const updateServiceInList = (updatedService) => {
    setServices((prev) => prev.map((s) => (s.service_id === updatedService.service_id ? updatedService : s)));
  };

  const changeImageStyle = async (newStyle) => {
    const previous = imageStyle;
    styleUserChangedRef.current = true;
    setImageStyle(newStyle);
    try {
      await axios.patch(`${API}/org/image-styles`, { catalog: "service", style: newStyle }, { withCredentials: true });
    } catch (err) {
      setImageStyle(previous);
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const regenerateAll = async () => {
    setRegeneratingAll(true);
    setError("");
    try {
      await axios.post(`${API}/org/services/images/regenerate-all`, {}, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRegeneratingAll(false);
    }
  };

  const hasAiImages = (services || []).some((s) => (s.images || []).some((img) => img.source === "ai_generated"));
  const editingService = (services || []).find((s) => s.service_id === editingServiceId) || null;
  const canOpenEdit = isOwner || canManageImages;

  return (
    <div className="space-y-6" data-testid="org-services-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">Services</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">What your organization sells or performs. Services can be used across Front Desk, Sales, Office and Billing.</p>
        </div>
        <LastUpdated timestamp={lastUpdated} testId="org-services-last-updated" />
      </div>

      {isManager && (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[3fr_2fr] lg:items-start">
      <Panel title="Your Services">
        {services === null ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400">Loading services…</div>
        ) : services.length === 0 ? (
          <EmptyState icon={Wrench} title="No services yet." message="Add your first service to get started." testId="org-services-empty" />
        ) : (
          <div className="space-y-2" data-testid="org-services-list">
            {services.map((s) => (
              <div key={s.service_id} className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid={`org-service-row-${s.service_id}`}>
                <button
                  type="button"
                  onClick={() => canOpenEdit && setEditingServiceId(s.service_id)}
                  disabled={!canOpenEdit}
                  data-testid={`org-service-open-edit-${s.service_id}`}
                  className={canOpenEdit ? "cursor-pointer" : "cursor-default"}
                >
                  <ServiceCoverThumb service={s} testId={`org-service-thumb-${s.service_id}`} />
                </button>
                <div className="min-w-[160px] flex-1">
                  <p className="text-sm font-bold text-slate-950 dark:text-white">{s.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {s.service_type ? `${s.service_type} · ` : ""}
                    {s.price_type === "custom_quote" ? "Custom quote" : `${s.price_type === "starting_at" ? "Starting at " : ""}${centsToDisplay(s.price_cents)}`}
                    {s.duration_minutes ? ` · ${s.duration_minutes} min` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {canOpenEdit && (
                    <button
                      type="button"
                      onClick={() => setEditingServiceId(s.service_id)}
                      data-testid={`org-service-edit-btn-${s.service_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleActive(s)}
                    disabled={savingId === s.service_id}
                    data-testid={`org-service-toggle-${s.service_id}`}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${s.active ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10" : "border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-white/15 dark:text-slate-400 dark:hover:bg-white/10"}`}
                  >
                    {s.active ? "Active" : "Inactive"}
                  </button>
                  {isManager && (
                    confirmDeleteId === s.service_id ? (
                      <div className="flex items-center gap-1.5" data-testid={`org-service-confirm-delete-${s.service_id}`}>
                        <button
                          type="button"
                          onClick={() => removeService(s)}
                          disabled={savingId === s.service_id}
                          data-testid={`org-service-confirm-delete-yes-${s.service_id}`}
                          className="rounded-lg bg-red-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          Delete?
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(null)}
                          data-testid={`org-service-confirm-delete-cancel-${s.service_id}`}
                          className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-500 dark:border-white/15 dark:text-slate-400"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(s.service_id)}
                        disabled={savingId === s.service_id}
                        data-testid={`org-service-delete-${s.service_id}`}
                        className="rounded-lg border border-red-200 p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
                      >
                        <Trash2 size={14} />
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel
        title="Add a Service"
        action={
          <ImageStyleSelector
            testIdPrefix="org-services"
            style={imageStyle}
            onChangeStyle={changeImageStyle}
            onRegenerateAll={regenerateAll}
            regenerating={regeneratingAll}
            hasAiImages={hasAiImages}
          />
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Service Name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-service-name-input" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Service Type</label>
            <input value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} data-testid="org-service-type-input" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Duration (minutes)</label>
            <input type="number" min="0" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="org-service-duration-input" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Pricing</label>
            <select value={form.price_type} onChange={(e) => setForm({ ...form, price_type: e.target.value })} data-testid="org-service-price-type" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
              {PRICE_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="org-service-description-input" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
          </div>
          {form.price_type !== "custom_quote" && (
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Price ($)</label>
              <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="org-service-price-input" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white" />
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Service Image (optional)</label>
            <div className="mt-1 flex items-center gap-3">
              {stagedPreview ? (
                <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-slate-200 dark:border-white/15">
                  <img src={stagedPreview} alt="Selected preview" className="h-full w-full object-cover" data-testid="org-service-create-image-preview" />
                  <button type="button" onClick={clearStagedFile} data-testid="org-service-create-image-remove-btn" className="absolute right-1 top-1 rounded-full bg-slate-950/70 p-0.5 text-white">
                    <X size={10} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="org-service-create-image-btn"
                  className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-dashed border-slate-300 text-slate-400 hover:bg-slate-50 dark:border-white/15 dark:text-slate-500 dark:hover:bg-white/10"
                >
                  <ImagePlus size={18} />
                </button>
              )}
              <input ref={fileInputRef} type="file" accept={ALLOWED_TYPES.join(",")} className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="org-service-create-image-input" />
              <p className="text-xs text-slate-400 dark:text-slate-500">Upload a photo, or leave blank and Nova will generate one automatically.</p>
            </div>
          </div>
        </div>
        {error && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-services-error">{error}</p>}
        {success && <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" data-testid="org-services-success">{success}</p>}
        <button type="button" onClick={addService} disabled={saving || !form.name} data-testid="org-service-add-btn" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">
          <Plus size={16} /> {saving ? "Adding…" : "Add Service"}
        </button>
        <AddingProgressBar active={saving} label="Adding service…" testId="org-service-adding-progress" />
      </Panel>
      </div>
      )}

      {!isManager && (
      <Panel title="Your Services">
        {services === null ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400">Loading services…</div>
        ) : services.length === 0 ? (
          <EmptyState icon={Wrench} title="No services yet." message="Ask your organization owner to add one." testId="org-services-empty" />
        ) : (
          <div className="space-y-2" data-testid="org-services-list">
            {services.map((s) => (
              <div key={s.service_id} className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid={`org-service-row-${s.service_id}`}>
                <button
                  type="button"
                  onClick={() => canOpenEdit && setEditingServiceId(s.service_id)}
                  disabled={!canOpenEdit}
                  data-testid={`org-service-open-edit-${s.service_id}`}
                  className={canOpenEdit ? "cursor-pointer" : "cursor-default"}
                >
                  <ServiceCoverThumb service={s} testId={`org-service-thumb-${s.service_id}`} />
                </button>
                <div className="min-w-[160px] flex-1">
                  <p className="text-sm font-bold text-slate-950 dark:text-white">{s.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {s.service_type ? `${s.service_type} · ` : ""}
                    {s.price_type === "custom_quote" ? "Custom quote" : `${s.price_type === "starting_at" ? "Starting at " : ""}${centsToDisplay(s.price_cents)}`}
                    {s.duration_minutes ? ` · ${s.duration_minutes} min` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {canOpenEdit && (
                    <button
                      type="button"
                      onClick={() => setEditingServiceId(s.service_id)}
                      data-testid={`org-service-edit-btn-${s.service_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                  <span data-testid={`org-service-status-${s.service_id}`} className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${s.active ? "border-emerald-200 text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400" : "border-slate-200 text-slate-500 dark:border-white/15 dark:text-slate-400"}`}>
                    {s.active ? "Active" : "Inactive"}
                  </span>
                  {isManager && (
                    confirmDeleteId === s.service_id ? (
                      <div className="flex items-center gap-1.5" data-testid={`org-service-confirm-delete-${s.service_id}`}>
                        <button type="button" onClick={() => removeService(s)} disabled={savingId === s.service_id} data-testid={`org-service-confirm-delete-yes-${s.service_id}`} className="rounded-lg bg-red-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50">Delete?</button>
                        <button type="button" onClick={() => setConfirmDeleteId(null)} data-testid={`org-service-confirm-delete-cancel-${s.service_id}`} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-500 dark:border-white/15 dark:text-slate-400">Cancel</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setConfirmDeleteId(s.service_id)} disabled={savingId === s.service_id} data-testid={`org-service-delete-${s.service_id}`} className="rounded-lg border border-red-200 p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10">
                        <Trash2 size={14} />
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
      )}

      {editingService && (
        <ServiceEditModal
          service={editingService}
          canEdit={isManager}
          canManageImages={canManageImages}
          onClose={() => setEditingServiceId(null)}
          onUpdated={updateServiceInList}
          onRefreshNow={() => load(true)}
        />
      )}
    </div>
  );
}
